/**
 * /enjoy api client · talks to /api/enjoy/* + /api/admin/enjoy/*.
 *
 * Uses the shared backend-url resolver so production custom domains
 * call same-origin (and the tenant header lands correctly).
 */
import { BACKEND_URL as BASE } from "../../lib/backend-url";

async function jsonOrThrow(res) {
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = j.detail || JSON.stringify(j);
    } catch (e) { /* fall back */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

export async function search({ tab, city, country, when, dateFrom, dateTo,
                              priceBand, q, page = 1, pageSize = 24, kind } = {}) {
  const p = new URLSearchParams();
  if (tab) p.set("tab", tab);
  if (city) p.set("city", city);
  if (country) p.set("country", country);
  if (when) p.set("when", when);
  if (dateFrom) p.set("date_from", dateFrom);
  if (dateTo) p.set("date_to", dateTo);
  if (priceBand) p.set("price_band", priceBand);
  if (q) p.set("q", q);
  if (kind) p.set("kind", kind);
  p.set("page", String(page));
  p.set("page_size", String(pageSize));
  return jsonOrThrow(await fetch(`${BASE}/api/enjoy/search?${p.toString()}`));
}

export async function enrich(source, eventId) {
  // Lazy enrichment fan-out · founder ruling 14 Feb 2026 PM-4. Called on
  // drawer-open only · the 24-card grid stays at 1 search call.
  return jsonOrThrow(await fetch(
    `${BASE}/api/enjoy/event/${encodeURIComponent(source)}/${encodeURIComponent(eventId)}/enrich`
  ));
}

export async function popular(limit = 8) {
  // Founder ruling 16 Feb 2026 PM-8 · desk-curated, verified-only rows
  // with priority>=90. Returns {events: [...], hide: bool}. If hide=true
  // the carousel renders nothing rather than padding with mock data.
  return jsonOrThrow(await fetch(`${BASE}/api/enjoy/popular?limit=${limit}`));
}

export async function signaturePublished(limit = 6) {
  // Founder ruling 16 Feb 2026 PM-6 · public Signature Experiences
  // catalogue · published rows only, desk-only fields stripped.
  return jsonOrThrow(await fetch(`${BASE}/api/signature?page_size=${limit}`));
}

export async function signatureCollection({ category, q, page = 1, pageSize = 200 } = {}) {
  // Founder ruling 16 Feb 2026 PM-20 · the full Signature Experiences
  // collection page at /enjoy/signature. Filters by top-level category
  // bucket (sport / arts / film / music / fashion / society / yachting /
  // collector_cars / awards / destination) and free-text search.
  const p = new URLSearchParams();
  if (category && category !== "all") p.set("category", category);
  if (q) p.set("q", q);
  p.set("page", String(page));
  p.set("page_size", String(pageSize));
  return jsonOrThrow(await fetch(`${BASE}/api/signature?${p.toString()}`));
}

export async function suggest(q) {
  if (!q || q.trim().length < 2) return { events: [], attractions: [], venues: [] };
  return jsonOrThrow(await fetch(
    `${BASE}/api/enjoy/suggest?q=${encodeURIComponent(q.trim())}`
  ));
}

export async function categories({ city, country } = {}) {
  const p = new URLSearchParams();
  if (city) p.set("city", city);
  if (country) p.set("country", country);
  return jsonOrThrow(await fetch(`${BASE}/api/enjoy/categories?${p.toString()}`));
}

export async function detail(source, eventId) {
  return jsonOrThrow(await fetch(`${BASE}/api/enjoy/event/${source}/${encodeURIComponent(eventId)}`));
}

export async function logIntent(payload) {
  // Founder ruling 16 Feb 2026 PM-22 · universal click-intent contract.
  // `keepalive: true` so telemetry survives the deep-link page transition.
  // We swallow ALL errors — telemetry MUST NEVER block the member journey
  // (see /app/memory/TELEMETRY_DOCTRINE.md §6).
  return fetch(`${BASE}/api/enjoy/intent`, {
    method: "POST",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

export async function requestViaConcierge(payload, memberCardNo) {
  const headers = { "Content-Type": "application/json" };
  if (memberCardNo) headers["X-Member-Card"] = memberCardNo;
  return jsonOrThrow(await fetch(`${BASE}/api/enjoy/request`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  }));
}
